import React, { useState, useContext, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaSearch, FaBoxOpen, FaTruck, FaMapMarkerAlt, FaQuestionCircle } from "react-icons/fa";
import MarketNavbar from "../Marketplace/MarketNav/MarketNavbar";
import MarketplacePage from "../Marketplace/components/MarketplacePage";
import useOrderByNumber from "../Marketplace/libs/useOrderByNumber";
import { AuthContext } from "../../Context/AuthContext";
import {
  STATUS_STEPS,
  formatShippingMethod,
  formatNgn,
} from "../Marketplace/libs/orderDisplayUtils";
import RetryPaymentButton from "../Marketplace/components/RetryPaymentButton";
import "./Tracking.css";

const STATUS_LABELS = {
  Pending: "Order placed",
  Processing: "Being prepared",
  Shipped: "On the way",
  Delivered: "Delivered",
  Canceled: "Canceled",
};

const PAYMENT_LABELS = {
  pending: "Awaiting payment",
  paid: "Paid",
  failed: "Payment failed",
  refunded: "Refunded",
};

const getItemImage = (item) =>
  item.image ||
  item.product?.images?.[0] ||
  "https://via.placeholder.com/72x72?text=STEX";

const Trackyourorder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoggedIn } = useContext(AuthContext);
  const initialOrder = searchParams.get("orderNumber") || "";
  const [orderNumber, setOrderNumber] = useState(initialOrder);
  const [submitted, setSubmitted] = useState(initialOrder);

  const { data: order, isLoading, isError } = useOrderByNumber(submitted, !!submitted);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = orderNumber.trim();
    setSubmitted(trimmed);
    if (trimmed) {
      setSearchParams({ orderNumber: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const currentStepIndex = order
    ? STATUS_STEPS.indexOf(order.status === "Canceled" ? "Pending" : order.status)
    : -1;

  const subtotal =
    order?.totalAmount != null ? order.totalAmount - (order.shippingCost || 0) : null;

  const destination =
    order?.shippingAddress?.city && order?.shippingAddress?.state
      ? `${order.shippingAddress.city}, ${order.shippingAddress.state}`
      : null;

  return (
    <Fragment>
      <MarketNavbar />
      <MarketplacePage className="track-order-page">
        <nav className="track-order-breadcrumb" aria-label="Breadcrumb">
          <Link to="/market">Home</Link>
          <span>/</span>
          <span>Track order</span>
        </nav>

        <header className="track-order-hero">
          <div className="track-order-hero__icon" aria-hidden="true">
            <FaTruck />
          </div>
          <div>
            <h1>Track your order</h1>
            <p>
              Enter your STEX order number to see live status, delivery progress, and item details.
              You will find it in your confirmation email or on your receipt.
            </p>
          </div>
        </header>

        <div className="track-order-layout">
          <section className="track-order-main">
            <div className="track-order-search-card">
              <form onSubmit={handleSubmit} className="track-order-search-form">
                <label htmlFor="order-number-input" className="track-order-search-label">
                  Order number
                </label>
                <div className="track-order-search-row">
                  <input
                    id="order-number-input"
                    type="text"
                    placeholder="e.g. STX-20250606-ABC123"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button type="submit" disabled={!orderNumber.trim() || isLoading}>
                    <FaSearch aria-hidden="true" />
                    <span>{isLoading && submitted ? "Searching..." : "Track"}</span>
                  </button>
                </div>
                <p className="track-order-search-hint">
                  Order numbers usually start with <strong>STX-</strong>. Copy it exactly as shown in your email.
                </p>
              </form>
            </div>

            {!submitted && (
              <div className="track-order-empty">
                <FaBoxOpen aria-hidden="true" />
                <h2>Ready when you are</h2>
                <p>Paste your order number above to view shipping progress and item status.</p>
              </div>
            )}

            {submitted && isLoading && (
              <div className="track-order-loading" aria-live="polite">
                <div className="track-order-loading__bar" />
                <div className="track-order-loading__bar track-order-loading__bar--short" />
                <p>Looking up your order...</p>
              </div>
            )}

            {submitted && !isLoading && isError && (
              <div className="track-order-not-found" role="alert">
                <h2>Order not found</h2>
                <p>
                  We could not find an order matching <strong>{submitted}</strong>. Check for typos,
                  extra spaces, or try the number from your confirmation email.
                </p>
                <div className="track-order-not-found__actions">
                  <Link to="/Contact">Contact support</Link>
                  <Link to="/market/return-policy">Return &amp; refund help</Link>
                </div>
              </div>
            )}

            {order && (
              <div className="track-order-result">
                <div className="track-order-result__header">
                  <div>
                    <p className="track-order-result__label">Order number</p>
                    <h2>{order.orderNumber}</h2>
                    {order.createdAt && (
                      <p className="track-order-result__date">
                        Placed {new Date(order.createdAt).toLocaleDateString(undefined, {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    )}
                  </div>
                  <div className="track-order-badges">
                    <span className={`track-badge track-badge--status track-badge--${order.status.toLowerCase()}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                    <span className={`track-badge track-badge--payment track-badge--${order.paymentStatus}`}>
                      {PAYMENT_LABELS[order.paymentStatus] || order.paymentStatus}
                    </span>
                  </div>
                </div>

                {order.status === "Canceled" ? (
                  <div className="track-order-canceled">
                    This order was canceled and will not be shipped.
                  </div>
                ) : (
                  <div className="track-order-timeline">
                    {STATUS_STEPS.map((step, idx) => {
                      const isComplete = idx < currentStepIndex;
                      const isCurrent = idx === currentStepIndex;
                      return (
                        <div
                          key={step}
                          className={`track-order-timeline__step ${isComplete ? "complete" : ""} ${isCurrent ? "current" : ""}`}
                        >
                          <div className="track-order-timeline__dot">
                            {isComplete ? "✓" : idx + 1}
                          </div>
                          <span className="track-order-timeline__label">{step}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="track-order-summary-grid">
                  <div className="track-order-summary-card">
                    <span className="track-order-summary-card__label">Shipping method</span>
                    <strong>{formatShippingMethod(order.shippingMethod)}</strong>
                    {order.shippingCost != null && (
                      <small>{formatNgn(order.shippingCost)}</small>
                    )}
                  </div>
                  {destination && (
                    <div className="track-order-summary-card">
                      <span className="track-order-summary-card__label">
                        <FaMapMarkerAlt aria-hidden="true" /> Destination
                      </span>
                      <strong>{destination}</strong>
                    </div>
                  )}
                  {subtotal != null && (
                    <div className="track-order-summary-card">
                      <span className="track-order-summary-card__label">Subtotal</span>
                      <strong>{formatNgn(subtotal)}</strong>
                    </div>
                  )}
                  <div className="track-order-summary-card track-order-summary-card--total">
                    <span className="track-order-summary-card__label">Order total</span>
                    <strong>{formatNgn(order.totalAmount)}</strong>
                  </div>
                </div>

                {order.items?.length > 0 && (
                  <div className="track-order-items">
                    <h3>Items in this order</h3>
                    <ul>
                      {order.items.map((item) => (
                        <li key={item._id}>
                          <img src={getItemImage(item)} alt="" />
                          <div className="track-order-item__info">
                            <span className="track-order-item__name">
                              {item.name}
                              {item.size ? ` · ${item.size}` : ""}
                            </span>
                            <span className="track-order-item__qty">Qty {item.quantity}</span>
                          </div>
                          <span className={`track-order-item__status track-badge track-badge--status track-badge--${(item.status || order.status).toLowerCase()}`}>
                            {item.status || order.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="track-order-actions">
                  {isLoggedIn && order._id && (
                    <Link to={`/account/orders/${order._id}`} className="track-order-actions__primary">
                      View in my account
                    </Link>
                  )}
                  {isLoggedIn && <RetryPaymentButton order={order} className="track-order-actions__primary" />}
                  {!isLoggedIn && (
                    <Link to="/SignUp" className="track-order-actions__primary">
                      Create account
                    </Link>
                  )}
                  <Link to="/market">Continue shopping</Link>
                </div>
              </div>
            )}
          </section>

          <aside className="track-order-aside">
            <div className="track-order-help-card">
              <FaQuestionCircle aria-hidden="true" />
              <h3>Need help?</h3>
              <ul>
                <li>
                  <Link to="/market/return-policy">Return &amp; refund policy</Link>
                </li>
                <li>
                  <Link to="/Contact">Contact support</Link>
                </li>
                <li>
                  <Link to="/market">Browse marketplace</Link>
                </li>
              </ul>
            </div>
            <div className="track-order-help-card track-order-help-card--muted">
              <h3>Where is my order number?</h3>
              <p>
                Check your order confirmation email from STEX, your payment receipt, or the order
                summary page right after checkout.
              </p>
            </div>
          </aside>
        </div>
      </MarketplacePage>
    </Fragment>
  );
};

export default Trackyourorder;
